import { cleanStep, isDocumentFilled, pruneEmptyNested } from "./steps";
import { extractLocalizedValue } from "./localize";

/**
 * Validate a tier's document requirements before submit.
 *
 * Two rules, both must pass:
 *   1. every `isRequired` document must be filled (hard mandatory).
 *   2. total filled documents must reach `tier.numberOfRequired`
 *      ("any N of M" — required docs count toward the total).
 *
 * Returns { ok: true } or { ok: false, messageKey, messageParams } so the
 * caller can surface a localized message.
 */
export const validateTierDocuments = (selectedTier, documents = []) => {
    const docSettings = selectedTier?.documentSettings || [];
    const isFilled = (s) => isDocumentFilled(documents.find((d) => d._id === s.documentSettingDoc?._id));

    // 1. all required docs must be filled
    const requiredEmpty = docSettings
        .filter((s) => s.isRequired && s.documentSettingDoc?._id)
        .find((s) => !isFilled(s));

    if (requiredEmpty) {
        return {
            ok: false,
            messageKey: "errorMsg.required.pleaseFillIn",
            messageParams: {
                field: extractLocalizedValue(requiredEmpty.documentSettingDoc.displayName) || requiredEmpty.documentSettingDoc.name,
            },
        };
    }

    // 2. total filled docs must reach numberOfRequired
    const needed = Math.min(selectedTier?.numberOfRequired || 0, docSettings.length);
    if (needed > 0 && docSettings.filter(isFilled).length < needed) {
        return {
            ok: false,
            messageKey: "accountVerification.pleaseCompleteAnySections",
            messageParams: { numberOfRequired: needed },
        };
    }

    return { ok: true };
};

/**
 * Collect every REQUIRED questionnaire step that must have a submitted record
 * before the verification can be submitted — including questionnaires nested
 * inside document_setting groups. Returns [{ recordId, fieldName }] for the
 * caller to validate asynchronously.
 */
export const collectRequiredQuestionnaires = (documentSettings = [], historyDocuments = []) => {
    const out = [];

    const walk = (steps, historySteps) => {
        for (const step of steps || []) {
            const field = step?.sourceDoc;
            if (!field) continue;
            const histStep = (historySteps || []).find((s) => s.sourceId === field._id);

            if (Array.isArray(field.steps) && field.steps.length > 0) {
                walk(field.steps, histStep?.value?.steps || []);
                continue;
            }

            if (step.isRequired && field.type === "questionnaire") {
                out.push({
                    recordId: histStep?.value,
                    fieldName: extractLocalizedValue(field.displayName) || field.name,
                });
            }
        }
    };

    for (const docSetting of documentSettings || []) {
        const docDef = docSetting?.documentSettingDoc;
        if (!docDef?.steps) continue;
        const docHistory = (historyDocuments || []).find((d) => d._id === docDef._id);
        walk(docDef.steps, docHistory?.steps || []);
    }

    return out;
};

/**
 * Build the submit payload: drop fully-empty documents, strip `invalid`, and
 * recurse nested step values via cleanStep.
 */
export const buildSubmitInput = (currentHistory, documents = null) => {
    const docs = documents || currentHistory?.documents || [];
    return {
        _id: currentHistory?._id,
        documents: docs
            // clean + collapse empty nested groups to value:null first...
            .map((doc) => ({
                ...doc,
                steps: (doc.steps || []).map((step) => pruneEmptyNested(cleanStep(step))),
            }))
            // ...then drop any document that is entirely empty after pruning
            .filter(isDocumentFilled),
    };
};
